import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient, apiClientText } from "./api-client.service";

import type {
  BulkInventoryTemplateResponse,
  BulkInventoryUploadFilter,
  BulkInventoryUploadPayload,
  BulkInventoryUploadResponse,
  UploadFilesResponse,
} from "../types/bulkinventoryupload.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.inventoryBulkUpload;

const TEMPLATE_FILE_NAME = "Inventory_Bulk_Upload_Template.csv";

// Saves CSV text as a file in the browser
const saveCsvFile = (csvText: string, fileName: string) => {
  const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const bulkInventoryUploadService = {
  async downloadTemplate(): Promise<BulkInventoryTemplateResponse> {
    try {
      const response = await apiClientText(endpoints.downloadTemplate("csv"), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });

      if (!response.success || !response.resData) {
        return {
          success: false,
          statusCode: response.statusCode,
          message: response.message || "Failed to download sample file.",
          resData: null,
        };
      }

      saveCsvFile(response.resData, TEMPLATE_FILE_NAME);

      return {
        success: true,
        statusCode: response.statusCode,
        message: "Sample file downloaded successfully.",
        resData: null,
      };
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to download sample file due to an unexpected error.",
        resData: null,
      };
    }
  },

  async upload(
    payload: BulkInventoryUploadPayload,
  ): Promise<BulkInventoryUploadResponse> {
    try {
      const formData = new FormData();
      formData.append("inventory_data", payload.inventory_data);
      formData.append("supplier_id", String(payload.supplier_id));
      
      return await apiClient<BulkInventoryUploadResponse>(endpoints.create, {
        method: "POST",
        body: formData,
        isAuthenticated: true,
      });
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to upload inventory file due to an unexpected error.",
        resData: null,
      };
    }
  },

  async listUploadHistory(
    filter: BulkInventoryUploadFilter,
  ): Promise<UploadFilesResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<UploadFilesResponse>(
        endpoints.listUploadHistory(params.toString()),
        {
          method: "GET",
          isAuthenticated: true,
          cache: "no-store",
        },
      );
    } catch (error) {
      return {
        success: false,
        statusCode: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to load upload history due to an unexpected error.",
        resData: { data: [], pagination: { total: 0, page: 1, limit: 10, totalPages: 1 } },
      };
    }
  },
};
